import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Button, Card, CardContent, CardDescription, CardHeader, useToast, Logo03Png, Logo01Png } from '@hiring-intelligence/ui';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { IS_MOCK_MODE, MOCK_TOKEN, MOCK_USER } from '@/mocks';

export default function LoginPage() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const login = useAuthStore((state) => state.login);
	const [isLoading, setIsLoading] = useState(false);

	// Staging demo only: signs in the mock test user — all data is served from
	// in-memory fixtures, nothing touches the API or database.
	const handleMockLogin = async () => {
		if (process.env.MOCK_MODE === 'true') {
			// Start every demo session from pristine fixtures.
			const { resetMockDb } = await import('@/mocks/data');
			resetMockDb();
		}
		login(MOCK_USER, MOCK_TOKEN);
		toast({ title: 'Welcome!', description: `Logged in as ${MOCK_USER.name} (test account, mock data)` });
		navigate('/');
	};

	const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
		if (!credentialResponse.credential) {
			toast({ title: 'Error', description: 'Google login failed — no credential received.', variant: 'destructive' });
			return;
		}

		setIsLoading(true);
		try {
			const response = await authApi.googleLogin(credentialResponse.credential);
			login(response.data.user, response.data.token);
			toast({ title: 'Welcome!', description: `Logged in as ${response.data.user.name}` });
			navigate('/');
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Authentication failed';
			const isBlocked = message.includes('blocked');
			const isDomainRestricted = message.includes('canismajor.asia');
			toast({
				title: isBlocked ? 'Account Blocked' : isDomainRestricted ? 'Access Denied' : 'Error',
				description: message,
				variant: 'destructive'
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="flex justify-center mb-4">
						<div className="rounded-full bg-primary/10 p-4">
							<img src={Logo03Png} alt="Canis Major" className="h-10 w-10" />
						</div>
					</div>
					<div className="flex justify-center">
						<img src={Logo01Png} alt="Canis Major" className="h-8" />
					</div>
					<CardDescription>
						Sign in with your @canismajor.asia Google account
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col items-center gap-4">
						{isLoading ? (
							<div className="text-sm text-muted-foreground">Signing in...</div>
						) : (
							<div className="w-full max-w-[320px]">
								<GoogleLogin
									onSuccess={handleGoogleSuccess}
									onError={() => {
										toast({ title: 'Error', description: 'Google sign-in failed. Please try again.', variant: 'destructive' });
									}}
									theme="outline"
									size="large"
									width={320}
									text="signin_with"
									shape="rectangular"
								/>
							</div>
						)}
						<p className="text-xs text-muted-foreground text-center">
							Only @canismajor.asia accounts are allowed.
							<br />
							Your account will be created automatically on first login.
						</p>
						{IS_MOCK_MODE && (
							<>
								<div className="w-full max-w-[320px] flex items-center gap-3">
									<div className="h-px flex-1 bg-border" />
									<span className="text-xs text-muted-foreground">or</span>
									<div className="h-px flex-1 bg-border" />
								</div>
								<Button variant="outline" className="w-full max-w-[320px]" onClick={handleMockLogin}>
									Sign in as Test User
								</Button>
								<p className="text-xs text-muted-foreground text-center">
									Demo account with mock data only — nothing is saved,
									<br />
									no database connection.
								</p>
							</>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
